import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import {
  houseConsignmentWithGoodsItemsSchema,
  TSDCreateMultipartDataSchema,
  TSDDuplicateDataSchema,
  TSDEditDataSchema,
  TSDInvalidateSchema,
  TSDListItemSchema,
  TSDMarkCustomsNotificationAsReadSchema,
  TSDRetrieveCriteriaSchema,
  TSDRevokePresentationNotificationSchema,
  TSDSchema,
  TSDSendPresentationNotificationSchema,
  TSDUploadHouseItemsDataSchema,
} from './schemas';

const c = initContract();

export const tsdContract = c.router({
  retrieveTsds: {
    method: 'POST',
    path: '/tsd',
    body: z.object({
      criteria: TSDRetrieveCriteriaSchema.optional(),
    }),
    responses: {
      200: z.object({
        data: z.object({
          list: z.array(TSDListItemSchema),
          totalItems: z.number(),
        }),
      }),
    },
  },
  retrieveTsd: {
    method: 'GET',
    path: '/tsd/:id',
    pathParams: z.object({ id: z.string() }),
    responses: {
      200: z.object({
        data: z.object({ tsd: TSDSchema }),
      }),
    },
  },
  createTsd: {
    method: 'POST',
    path: '/tsd/add',
    body: TSDCreateMultipartDataSchema,
    contentType: 'multipart/form-data',
    responses: {
      200: z.object({
        data: z.object({
          createdTsd: TSDListItemSchema,
        }),
      }),
      400: z.object({
        data: z.object({ message: z.string() }),
        issues: z.array(
          z
            .object({
              code: z.string(),
              message: z.string(),
              path: z.array(z.union([z.string(), z.number()])),
            })
            .passthrough(),
        ),
      }),
    },
  },
  updateTsd: {
    method: 'PATCH',
    path: '/tsd',
    body: TSDEditDataSchema,
    responses: {
      200: z.object({
        data: z.object({ updatedTsd: TSDSchema }),
      }),
      404: z.object({ message: z.string() }),
    },
  },
  deleteTsd: {
    method: 'DELETE',
    path: '/tsd/:tsdId',
    body: c.noBody(),
    pathParams: z.object({ tsdId: z.string() }),
    responses: {
      204: z.object({}),
    },
  },
  duplicateTsd: {
    method: 'POST',
    path: '/tsd/duplicate',
    body: TSDDuplicateDataSchema,
    responses: {
      200: z.object({
        data: z.object({ duplicatedTsdId: z.string() }),
      }),
    },
  },
  sendTsd: {
    method: 'POST',
    path: '/tsd/:tsdId/send-tsd',
    pathParams: z.object({ tsdId: z.string() }),
    body: c.noBody(),
    responses: {
      200: z.object({
        data: z.object({ message: z.string() }),
      }),
    },
  },
  customsSyncTsd: {
    method: 'POST',
    path: '/tsd/:tsdId/customs-sync',
    pathParams: z.object({ tsdId: z.string() }),
    body: c.noBody(),
    responses: {
      200: z.object({
        data: z.object({ message: z.string() }),
      }),
    },
  },
  uploadHouseItems: {
    method: 'POST',
    path: '/tsd/:tsdId/:houseConsignmentIndex/upload-house-items',
    body: TSDUploadHouseItemsDataSchema,
    contentType: 'multipart/form-data',
    responses: {
      200: z.object({
        data: houseConsignmentWithGoodsItemsSchema,
      }),
      400: z.object({
        data: z.object({ message: z.string() }),
      }),
    },
  },
  downloadHouseItems: {
    method: 'GET',
    path: '/tsd/:tsdId/:houseConsignmentIndex/download-house-items',
    responses: {
      200: c.otherResponse({
        contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        body: z.any(),
      }),
      400: z.object({
        data: z.object({ message: z.string() }),
      }),
    },
  },
  sendPresentationNotification: {
    method: 'POST',
    path: '/tsd/send-presentation-notification',
    body: TSDSendPresentationNotificationSchema,
    responses: {
      200: z.object({
        data: z.object({ message: z.string() }),
      }),
    },
  },
  revokePresentationNotification: {
    method: 'POST',
    path: '/tsd/revoke-presentation-notification',
    body: TSDRevokePresentationNotificationSchema,
    responses: {
      200: z.object({
        data: z.object({ message: z.string() }),
      }),
    },
  },
  invalidate: {
    method: 'POST',
    path: '/tsd/invalidate',
    body: TSDInvalidateSchema,
    responses: {
      200: z.object({
        data: z.object({ message: z.string(), id: z.string() }),
      }),
    },
  },
  markCustomsNotificationAsRead: {
    method: 'PATCH',
    path: '/tsd/:tsdId/notifications/:notificationId/read',
    pathParams: TSDMarkCustomsNotificationAsReadSchema,
    body: c.noBody(),
    responses: {
      200: z.object({
        data: z.object({ message: z.string(), id: z.string() }),
      }),
    },
  },
  amendTsd: {
    method: 'POST',
    path: '/tsd/:tsdId/amend',
    pathParams: z.object({ tsdId: z.string() }),
    body: c.noBody(),
    responses: {
      200: z.object({
        data: z.object({}),
      }),
    },
  },
  cancelAmendment: {
    method: 'POST',
    path: '/tsd/:tsdId/cancel-amendment',
    pathParams: z.object({ tsdId: z.string() }),
    body: c.noBody(),
    responses: {
      200: z.object({
        data: z.object({}),
      }),
    },
  },
  sendDeconsolidation: {
    method: 'POST',
    path: '/tsd/:tsdId/send-deconsolidation',
    pathParams: z.object({ tsdId: z.string() }),
    body: c.noBody(),
    responses: {
      200: z.object({
        data: z.object({}),
      }),
    },
  },
});
